import { CloseRounded } from "@mui/icons-material";
import { IconButton, MobileStepper, Stack } from "@mui/material";
import { useEffect } from "react";
import { COLOR_MAIN } from "../../../data/colors/Colors";
import { CustomButtonView } from "../../component/CustomButtonView";
import { NewGamePage, NewGameViewModel } from "./NewGameViewModel";
import { useTranslation } from "react-i18next";
import { CustomCardView } from "../../component/CustomCardView";
import { CustomTextView } from "../../component/CustomTextView";
import { ChooseTeamsNameView } from "./chooseTeamsName/ChooseTeamsNameView";
import { ChooseCategoriesView } from "./chooseCategories/ChooseCategoriesView";
import { Category, Subcategory } from "../../../domain/model/Category";
import {
  provideGetAllCategoriesUseCase,
  provideGetNumberOfRemainingQuestionsUseCase,
  providePlayerAddToGamesUseCase,
  providePlayerGameStartedUseCase,
} from "../../di/UseCasesModule";
import { SettingsView } from "./settingsPage/SettingsView";
import { ChooseLifelinesView } from "./chooseLifelines/ChooseLifelinesView";
import { Lifeline } from "../../../domain/model/Lifeline";
import { Difficulty } from "../../../domain/model/Question";
import useWindowDimensions from "../../../core/utils/UseWindowDimensions";
import { Player } from "../../../domain/model/User";
import { isMobile } from "react-device-detect";

export function NewGameView({
  handleClose,
  handleHeightChanged,
  player,
  shouldChooseCategoryDirectly,
}: {
  handleClose: () => void;
  handleHeightChanged: (height: string) => void;
  player: Player;
  shouldChooseCategoryDirectly: boolean;
}) {
  const viewModel = NewGameViewModel(
    provideGetAllCategoriesUseCase(),
    provideGetNumberOfRemainingQuestionsUseCase(),
    providePlayerGameStartedUseCase(),
    providePlayerAddToGamesUseCase(),
    shouldChooseCategoryDirectly
  );

  const { t } = useTranslation();

  const { width } = useWindowDimensions();

  const activeStep = viewModel.activeStep;
  const handleNext = viewModel.handleNext;
  const handleBack = viewModel.handleBack;

  useEffect(() => {
    switch (viewModel.currentPage) {
      case NewGamePage.CHOOSE_CATEGORIES: {
        handleHeightChanged("100%");
        break;
      }
      default: {
        handleHeightChanged(width < 700 ? "80%" : "65%");
        break;
      }
    }
  }, [viewModel.currentPage]);

  return (
    <Stack
      width={"100%"}
      alignItems={"center"}
      p={1}
      height={"100%"}
      justifyContent={"space-between"}
      spacing={1}
    >
      <CustomCardView
        width={"100%"}
        padding={2}
        borderRadius={4}
        background={COLOR_MAIN}
        children={
          <Stack
            width={"100%"}
            height={"100%"}
            justifyContent={"space-between"}
            alignItems={"center"}
            direction={"row"}
          >
            <CustomTextView
              text={CurrentPageTitleView({
                currentPage: viewModel.currentPage,
              })}
              fontWeight="bold"
              variant="h5"
              textColor="white"
            />
            <IconButton
              onClick={handleClose}
              sx={{ width: 40, height: 40, background: "rgba(135,40,77,1)" }}
            >
              <CloseRounded sx={{ color: "white" }} />
            </IconButton>
          </Stack>
        }
      />
      <CustomCardView
        width={"100%"}
        padding={0}
        borderRadius={4}
        height={"100%"}
        children={
          <CurrentPageView
            player={player}
            currentPage={viewModel.currentPage}
            onSettingsClicked={viewModel.handleOnSettingsClicked}
            firstTeamName={viewModel.firstTeamName}
            secondTeamName={viewModel.secondTeamName}
            onFirstTeamNameChanged={viewModel.handleOnFirstTeamNameChanged}
            onSecondTeamNameChanged={viewModel.handleOnSecondTeamNameChanged}
            categories={viewModel.categories}
            setSelectedCategory={(c) => viewModel.setSelectedCategory(c)}
            selectedCategory={viewModel.selectedCategory}
            loading={viewModel.loading}
            firstTeamLifelines={viewModel.firstTeamLifelines}
            secondTeamLifelines={viewModel.secondTeamLifelines}
            onCategoryClicked={viewModel.onCategoryClicked}
            onCategoryDoubleClicked={viewModel.onCategoryDoubleClicked}
            onSubcategoryClicked={viewModel.onSubcategoryClicked}
            handleOnLifelinesClicked={viewModel.handleOnLifelinesClicked}
            shouldPlaySounds={viewModel.shouldPlaySounds}
            onShouldPlaySoundsChanged={(e) => viewModel.setShouldPlaySounds(e)}
            canAnyTeamChooseAnyQuestion={viewModel.canAnyTeamChooseAnyQuestion}
            onCanAnyTeamChooseAnyQuestionChanged={(e) =>
              viewModel.setCanAnyTeamChooseAnyQuestion(e)
            }
            difficultiesMap={viewModel.difficultiesMap}
            handleDifficultiesTimeChanged={(difficulty, value) => {
              const diffMap = viewModel.difficultiesMap;
              diffMap.set(difficulty, value);
              viewModel.setDifficultiesMap(diffMap);
            }}
            shouldChooseCategoryDirectly={shouldChooseCategoryDirectly}
          />
        }
      />
      <CustomCardView
        width={"100%"}
        padding={2}
        borderRadius={4}
        background={COLOR_MAIN}
        children={
          <Stack
            alignItems={"center"}
            width={"100%"}
            height={"100%"}
            justifyContent={"center"}
          >
            <MobileStepper
              variant="progress"
              steps={viewModel.totalPages}
              position="static"
              activeStep={activeStep}
              sx={{
                width: "100%",
                background: "transparent",
              }}
              LinearProgressProps={{
                sx: {
                  borderRadius: 20,
                  backgroundColor: "rgba(255, 255, 255, 0.3)",
                  width: "100%",
                  marginLeft: 1,
                  marginRight: 1,
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: "white",
                    borderRadius: 20,
                  },
                },
              }}
              nextButton={
                <CustomButtonView
                  text={
                    activeStep === viewModel.totalPages - 1
                      ? t("start")
                      : t("next")
                  }
                  onClick={
                    activeStep === viewModel.totalPages - 1
                      ? () => viewModel.startGame(width < 700)
                      : handleNext
                  }
                  fontSize="button"
                  backgroundColor="rgba(135,40,77,1)"
                  loading={viewModel.gameStartingLoading}
                />
              }
              backButton={
                <CustomButtonView
                  text={t("previous")}
                  onClick={handleBack}
                  fontSize="button"
                  disabled={activeStep === 0 || viewModel.gameStartingLoading}
                  props={{ paddingLeft: 4, paddingRight: 4 }}
                  backgroundColor="rgba(135,40,77,1)"
                />
              }
            />
          </Stack>
        }
      />
    </Stack>
  );
}

function CurrentPageTitleView({
  currentPage,
}: {
  currentPage: NewGamePage;
}): string {
  const { t } = useTranslation();
  switch (currentPage) {
    case NewGamePage.CHOOSE_NAME:
      return t("choose_teams_name");
    case NewGamePage.SETTINGS:
      return t("settings");
    case NewGamePage.CHOOSE_CATEGORIES:
      return t("choose_categories");
    case NewGamePage.CHOOSE_LIFELINES:
      return t("choose_lifelines");
  }
}

function CurrentPageView({
  player,
  currentPage,
  onSettingsClicked,
  firstTeamName,
  secondTeamName,
  onFirstTeamNameChanged,
  onSecondTeamNameChanged,
  shouldPlaySounds,
  onShouldPlaySoundsChanged,
  canAnyTeamChooseAnyQuestion,
  onCanAnyTeamChooseAnyQuestionChanged,
  difficultiesMap,
  handleDifficultiesTimeChanged,
  categories,
  selectedCategory,
  setSelectedCategory,
  loading,
  firstTeamLifelines,
  secondTeamLifelines,
  onCategoryClicked,
  onCategoryDoubleClicked,
  onSubcategoryClicked,
  handleOnLifelinesClicked,
  shouldChooseCategoryDirectly,
}: {
  player: Player;
  currentPage: NewGamePage;
  onSettingsClicked: () => void;
  firstTeamName: string;
  secondTeamName: string;
  onFirstTeamNameChanged: (txt: string) => void;
  onSecondTeamNameChanged: (txt: string) => void;
  shouldPlaySounds: boolean;
  onShouldPlaySoundsChanged: (can: boolean) => void;
  canAnyTeamChooseAnyQuestion: boolean;
  onCanAnyTeamChooseAnyQuestionChanged: (can: boolean) => void;
  difficultiesMap: Map<Difficulty, number>;
  handleDifficultiesTimeChanged: (
    difficulty: Difficulty,
    value: number
  ) => void;
  categories: Array<Category>;
  selectedCategory: Category | undefined;
  setSelectedCategory: (category: Category) => void;
  loading: boolean;
  firstTeamLifelines: Array<Lifeline>;
  secondTeamLifelines: Array<Lifeline>;
  onCategoryClicked: (category: Category, closePopover?: () => void) => void;
  onCategoryDoubleClicked: (category: Category) => void;
  onSubcategoryClicked: (category: Category, subcategory: Subcategory) => void;
  handleOnLifelinesClicked: (
    lifelineId: string,
    isForFirstTeam: boolean
  ) => void;
  shouldChooseCategoryDirectly: boolean;
}) {
  switch (currentPage) {
    case NewGamePage.CHOOSE_NAME:
      return (
        <ChooseTeamsNameView
          onSettingsClicked={onSettingsClicked}
          onFirstTeamNameChanged={onFirstTeamNameChanged}
          onSecondTeamNameChanged={onSecondTeamNameChanged}
          firstTeamName={firstTeamName}
          secondTeamName={secondTeamName}
        />
      );
    case NewGamePage.SETTINGS:
      return (
        <SettingsView
          shouldPlaySounds={shouldPlaySounds}
          onShouldPlaySoundsChanged={onShouldPlaySoundsChanged}
          canAnyTeamChooseAnyQuestion={canAnyTeamChooseAnyQuestion}
          onCanAnyTeamChooseAnyQuestionChanged={
            onCanAnyTeamChooseAnyQuestionChanged
          }
          difficultiesMap={difficultiesMap}
          handleDifficultiesTimeChanged={handleDifficultiesTimeChanged}
        />
      );
    case NewGamePage.CHOOSE_CATEGORIES:
      return (
        <ChooseCategoriesView
          player={player}
          categories={categories}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          loading={loading}
          onCategoryClicked={onCategoryClicked}
          onSubcategoryClicked={onSubcategoryClicked}
          onCategoryDoubleClicked={onCategoryDoubleClicked}
          shouldChooseCategoryDirectly={shouldChooseCategoryDirectly}
        />
      );
    case NewGamePage.CHOOSE_LIFELINES:
      return (
        <ChooseLifelinesView
          firstTeamLifelines={firstTeamLifelines}
          secondTeamLifelines={secondTeamLifelines}
          firstTeamName={firstTeamName}
          secondTeamName={secondTeamName}
          handleOnLifelinesClicked={handleOnLifelinesClicked}
        />
      );
  }
}
